import { useEffect, useRef } from 'react'
import { X, Mail, Calendar, User, Building2, ExternalLink } from 'lucide-react'
import type { OutreachEvent } from '@/types/outreach'
import { EVENT_TYPE_CONFIG } from '@/types/outreach'
import type { OutreachEventType } from '@/types/outreach'

interface EmailPreviewModalProps {
  event: OutreachEvent | null
  onClose: () => void
}

function formatDate(iso: string) {
  if (!iso) return '\u2014'
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

/** Inject <base target="_blank"> so every link in the HTML opens in a new tab. */
function prepareHtmlForPreview(html: string): string {
  const baseTag = '<base target="_blank" rel="noopener noreferrer">'
  if (html.toLowerCase().includes('<head>')) {
    return html.replace(/<head>/i, `<head>${baseTag}`)
  }
  if (html.toLowerCase().includes('<html>')) {
    return html.replace(/<html>/i, `<html><head>${baseTag}</head>`)
  }
  // No head/html tags — prepend
  return `<head>${baseTag}</head>${html}`
}

/** Convert plain-text URLs into clickable <a> tags. */
function linkifyText(text: string): string {
  const urlRegex = /(https?:\/\/[^\s<]+)/g
  return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline">$1</a>')
}

export default function EmailPreviewModal({ event, onClose }: EmailPreviewModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Auto-resize iframe to content height
  useEffect(() => {
    if (!iframeRef.current) return
    const iframe = iframeRef.current

    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        if (doc?.body) {
          const height = doc.body.scrollHeight + 40
          iframe.style.height = `${Math.min(Math.max(height, 200), window.innerHeight * 0.65)}px`
        }
      } catch {
        // cross-origin — keep default height
      }
    }

    iframe.addEventListener('load', handleLoad)
    return () => iframe.removeEventListener('load', handleLoad)
  }, [event])

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (event) {
      document.addEventListener('keydown', handleKey)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [event, onClose])

  if (!event) return null

  const cfg = EVENT_TYPE_CONFIG[event.event_type as OutreachEventType]
  const meta = (event.metadata || {}) as Record<string, string>
  const contactName = event.contact_name || meta.contact_name || '\u2014'
  const body = event.body || ''
  const isHtml = body.includes('<') && (body.includes('</') || body.includes('/>'))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div
        className="relative w-full max-w-4xl max-h-[90vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Mail className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm truncate pr-4">{event.subject || 'Sem assunto'}</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                {cfg && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{ backgroundColor: (cfg.color || '#666') + '20', color: cfg.color }}
                  >
                    {cfg.title}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(event.created_at)}
                </span>
                <span className={event.direction === 'inbound' ? 'text-green-400' : 'text-blue-400'}>
                  {event.direction === 'inbound' ? '\u2190 Entrada' : '\u2192 Sa\u00edda'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-accent transition-colors flex-shrink-0"
            title="Fechar (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Meta info bar */}
        <div className="px-4 py-2 border-b bg-muted/10 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground flex-shrink-0">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <strong>Contato:</strong> {contactName}
          </span>
          {event.lead_title && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              <strong>Lead:</strong> {event.lead_title}
            </span>
          )}
          {event.lead_company && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              <strong>Empresa:</strong> {event.lead_company}
            </span>
          )}
          {event.channel && <span><strong>Canal:</strong> {event.channel}</span>}
          {event.contact_linkedin_url && (
            <a
              href={event.contact_linkedin_url.startsWith('http') ? event.contact_linkedin_url : `https://${event.contact_linkedin_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[#0A66C2] hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> LinkedIn
            </a>
          )}
        </div>

        {/* Body content */}
        <div className="flex-1 overflow-auto p-4 min-h-0">
          {body ? (
            isHtml ? (
              <div className="bg-white rounded-lg border overflow-hidden">
                <iframe
                  ref={iframeRef}
                  srcDoc={prepareHtmlForPreview(body)}
                  className="w-full border-0"
                  style={{ minHeight: '300px', height: '55vh' }}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                  title="Email preview"
                />
              </div>
            ) : (
              <div
                className="whitespace-pre-wrap text-sm leading-relaxed bg-muted/30 rounded-lg p-5 border break-words"
                dangerouslySetInnerHTML={{ __html: linkifyText(body) }}
              />
            )
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Sem conte\u00fado dispon\u00edvel para este evento.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-muted/30 flex items-center justify-between text-xs text-muted-foreground flex-shrink-0">
          <span>ID: {event.id?.slice(0, 8)}...</span>
          <div className="flex items-center gap-2">
            {isHtml && body && (
              <button
                onClick={() => {
                  const w = window.open('', '_blank')
                  if (w) { w.document.write(prepareHtmlForPreview(body)); w.document.close() }
                }}
                className="px-3 py-1.5 border rounded-md text-xs font-medium hover:bg-accent transition-colors flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> Abrir em nova janela
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:opacity-90 transition-opacity"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
